const EDITABLE_TAGS = /* @__PURE__ */ new Set(["INPUT", "TEXTAREA", "SELECT"]);
function isEditableTarget(target) {
  if (!target || typeof target !== "object") return false;
  const el = target;
  if (el.isContentEditable === true) return true;
  if (typeof el.tagName !== "string" || !EDITABLE_TAGS.has(el.tagName.toUpperCase())) return false;
  const type = typeof el.type === "string" ? el.type.toLowerCase() : "text";
  return !["button", "submit", "reset", "checkbox", "radio", "range", "color", "file"].includes(
    type
  );
}
function matches(combo, e) {
  if (combo.code !== void 0 && e.code !== combo.code) return false;
  if (combo.key !== void 0 && e.key !== combo.key) return false;
  if (Boolean(combo.alt) !== e.altKey) return false;
  if (Boolean(combo.mod) !== (e.ctrlKey || e.metaKey)) return false;
  if (combo.code !== void 0 && Boolean(combo.shift) !== e.shiftKey) return false;
  return true;
}
function comboLabel(combo, platform = "other") {
  const parts = [];
  if (combo.mod) parts.push(platform === "mac" ? "\u2318" : "Ctrl");
  if (combo.alt) parts.push(platform === "mac" ? "\u2325" : "Alt");
  if (combo.shift) parts.push("Shift");
  const code = combo.code ?? "";
  const key = combo.key ?? (code.startsWith("Digit") ? code.slice(5) : code.startsWith("Key") ? code.slice(3) : code === "Escape" ? "Esc" : code.replace("Arrow", ""));
  parts.push(key === "Up" ? "\u2191" : key === "Down" ? "\u2193" : key);
  return parts.join("+");
}
class ShortcutRegistry {
  entries = /* @__PURE__ */ new Map();
  enabled = true;
  listeners = /* @__PURE__ */ new Set();
  /** Registers a handler; the most recent registration of an id wins until it is removed. */
  register(definition, handler) {
    const entry = this.entries.get(definition.id) ?? { definition, handlers: [] };
    entry.definition = definition;
    entry.handlers.push(handler);
    this.entries.set(definition.id, entry);
    this.notify();
    return () => {
      const current = this.entries.get(definition.id);
      if (!current) return;
      current.handlers = current.handlers.filter((h) => h !== handler);
      if (current.handlers.length === 0) this.entries.delete(definition.id);
      this.notify();
    };
  }
  setEnabled(enabled) {
    this.enabled = enabled;
    this.notify();
  }
  get isEnabled() {
    return this.enabled;
  }
  /** Active shortcuts, for the help dialog. */
  list() {
    return [...this.entries.values()].map((e) => e.definition);
  }
  subscribe(listener) {
    this.listeners.add(listener);
    return () => {
      this.listeners.delete(listener);
    };
  }
  /** Runs the matching handler; returns true (and prevents the default) when one ran. */
  handleKeyDown(e) {
    if (!this.enabled || e.isComposing || e.defaultPrevented) return false;
    const editable = isEditableTarget(e.target);
    for (const { definition, handlers } of this.entries.values()) {
      if (editable && !definition.allowInInputs) continue;
      if (!definition.combos.some((c) => matches(c, e))) continue;
      const handler = handlers[handlers.length - 1];
      if (!handler) continue;
      e.preventDefault();
      handler();
      return true;
    }
    return false;
  }
  notify() {
    for (const listener of this.listeners) listener();
  }
}
const SHELL_SHORTCUTS = {
  search: {
    id: "search",
    combos: [{ code: "KeyK", mod: true }, { key: "/" }],
    labelKey: "ui:shortcuts.search",
    group: "search"
  },
  toggleListPane: {
    id: "toggle-list-pane",
    combos: [{ code: "KeyL", alt: true }],
    labelKey: "ui:shortcuts.toggleListPane",
    group: "navigation"
  },
  newChat: {
    id: "new-chat",
    combos: [{ code: "KeyN", alt: true }],
    labelKey: "ui:shortcuts.newChat",
    group: "general"
  },
  previousItem: {
    id: "previous-item",
    combos: [{ code: "ArrowUp", alt: true }],
    labelKey: "ui:shortcuts.previousItem",
    group: "navigation"
  },
  nextItem: {
    id: "next-item",
    combos: [{ code: "ArrowDown", alt: true }],
    labelKey: "ui:shortcuts.nextItem",
    group: "navigation"
  },
  close: {
    id: "close",
    combos: [{ code: "Escape" }],
    labelKey: "ui:shortcuts.close",
    group: "general",
    allowInInputs: true
  },
  help: { id: "help", combos: [{ key: "?" }], labelKey: "ui:shortcuts.help", group: "general" }
};
const appShortcut = (index) => ({
  id: `app-${String(index)}`,
  combos: [{ code: `Digit${String(index)}`, alt: true }],
  labelKey: `ui:shortcuts.app${String(index)}`,
  group: "navigation"
});
export {
  SHELL_SHORTCUTS,
  ShortcutRegistry,
  appShortcut,
  comboLabel,
  isEditableTarget,
  matches
};
//# sourceMappingURL=registry.js.map